import { AppContext, Features } from "@lightspeed-invoicing/core-client/frontend"
import { defineMessages } from "react-intl"
import { ColumnDefinition, ColumnDefinitionCategory, DataType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import GlobalHelper from "../../../../common/GlobalHelper"
import { LabelBadges } from "../../../../common/label/LabelBadges"
import { Settings, SupportedSettings } from "../../../../settings/account/Settings"
import { PRODUCT_TYPE } from "../../../product/Types"
import { PREFERENCE_TYPE } from "../../constants"
    
export enum ProductsColumnIds {
    IMAGE = "imageIds",
    TITLE = "title",
    SHORT_DESCRIPTION = "shortDescription",
    LABELS = "labels",
    INVENTORY = "inventory",
    PRICE = "price",
    ARCHIVED = "isArchived",
    SKU = "sku",
    MANUFACTURER_SKU = "manufacturerSku",
    UPDATED_AT_DATE = "updatedAt",
    BARCODE = "barcode",
}

const inventoryEnabled = GlobalHelper.isQBOInventorySyncVisible(
    AppContext.hasFeatureIntegration(Features.InventorySync)
) || AppContext.isInventoryEnabled()
const showShortDescription = !!Settings.get(SupportedSettings.ShowShortDescriptionInListing)

const columnDefinitions: ColumnDefinition[] = [
    {
        id: ProductsColumnIds.IMAGE,
        accessorFn: row => row.imageIds?.length && row.imageIds[0],
        dataType: DataType.IMAGE,
        sortable: false,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: ProductsColumnIds.TITLE,
        sortId: "titleKeyword",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/products/edit/{id}",
            secondaryValueKey: ProductsColumnIds.SKU,
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: ProductsColumnIds.SHORT_DESCRIPTION,
        sortId: "shortDescriptionKeyword",
        dataType: DataType.STRING,
        sortable: true,
        defaultView: showShortDescription,
        categories: [
            ...(showShortDescription ? [ColumnDefinitionCategory.DEFAULT] : []),
            ColumnDefinitionCategory.PRODUCT_RELATED,
        ],
    },
    {
        id: ProductsColumnIds.LABELS,
        dataType: DataType.COMPONENT,
        dataAttributes: {
            component: LabelBadges,
            props: {
                data: ProductsColumnIds.LABELS
            }
        },
        sortable: false,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: ProductsColumnIds.INVENTORY,
        dataType: DataType.INVENTORY,
        sortable: false,
        defaultView: inventoryEnabled,
        categories: [
            ...(inventoryEnabled ? [ColumnDefinitionCategory.DEFAULT] : []),
            ColumnDefinitionCategory.PRODUCT_RELATED,
        ],
    },
    {
        id: ProductsColumnIds.PRICE,
        sortId: "defaultPrice",
        dataType: DataType.PRICING,
        dataAttributes: {
            textAlign: "right",
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: ProductsColumnIds.ARCHIVED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: ProductsColumnIds.SKU,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: ProductsColumnIds.MANUFACTURER_SKU,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: ProductsColumnIds.UPDATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: ProductsColumnIds.BARCODE,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
]

const headerMessages = defineMessages({
    [ProductsColumnIds.IMAGE]: {
        id: "generic-listing.products.table-header.image",
        defaultMessage: "Image",
    },
    [ProductsColumnIds.TITLE]: {
        id: "generic-listing.products.table-header.title",
        defaultMessage: "Title",
    },
    [ProductsColumnIds.SHORT_DESCRIPTION]: {
        id: "generic-listing.products.table-header.short-description",
        defaultMessage: "Short Description",
    },
    [ProductsColumnIds.LABELS]: {
        id: "generic-listing.products.table-header.labels",
        defaultMessage: "Labels",
    },
    [ProductsColumnIds.INVENTORY]: {
        id: "generic-listing.products.table-header.inventory",
        defaultMessage: "Inventory",
    },
    [ProductsColumnIds.PRICE]: {
        id: "generic-listing.products.table-header.price",
        defaultMessage: "Price",
    },
    [ProductsColumnIds.ARCHIVED]: {
        id: "generic-listing.products.table-header.archived",
        defaultMessage: "Archived",
    },
    [ProductsColumnIds.SKU]: {
        id: "generic-listing.products.table-header.sku",
        defaultMessage: "SKU",
    },
    [ProductsColumnIds.MANUFACTURER_SKU]: {
        id: "generic-listing.products.table-header.manufacturer-sku",
        defaultMessage: "Manufacturer SKU",
    },
    [ProductsColumnIds.UPDATED_AT_DATE]: {
        id: "generic-listing.products.table-header.updated-at-date",
        defaultMessage: "Updated At Date",
    },
    [ProductsColumnIds.BARCODE]: {
        id: "generic-listing.products.table-header.barcode",
        defaultMessage: "Barcode",
    },
})

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.PRODUCTS,
    columnDefinitions,
    headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_PRODUCTS,
    searchPlaceholder: {
        id: "generic-listing.products.search-placeholder",
        defaultMessage: "Search products or services...",
    },
    displayString: {
        id: "generic-listing.products.display-string",
        defaultMessage: "Products",
    },
    searchObjectType: ObjectType.PRODUCTS,
    bulkActionDefinitions: [],
    additionalFilters: {
        type: [PRODUCT_TYPE.SINGLE]
    },
    exportModalComponent: require("../../../../common/listing/generic-listing/export-modals/ProductExportModal"),
    creationButtonComponent: require("../../../../common/listing/generic-listing/creation-buttons/ProductCreationButton"),
}

export default configuration


// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/products.ts